import { useState, useCallback } from "react";
import { VendorEntity, GetVendorByIdUseCase } from "../../domain";
import { VendorRepositoryImpl } from "../../data/repositories/VendorRepositoryImpl";

// Create repository instance (singleton)
const vendorRepository = new VendorRepositoryImpl();

// Create use case
const getVendorByIdUseCase = new GetVendorByIdUseCase(vendorRepository);

export interface UseVendorDetailsReturn {
  vendor: VendorEntity | null;
  loading: boolean;
  error: string | null;

  fetchVendor: (vendorId: string) => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

export const useVendorDetails = (): UseVendorDetailsReturn => {
  const [vendor, setVendor] = useState<VendorEntity | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch vendor by ID
   */
  const fetchVendor = useCallback(async (vendorId: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const result = await getVendorByIdUseCase.execute(vendorId);

      if (!result.success || !result.data) {
        throw new Error("Vendor not found");
      }

      setVendor(result.data);
    } catch (error: any) {
      const errorMessage = error.message || "Failed to load vendor";
      setError(errorMessage);
      console.error("Failed to fetch vendor:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Clear error
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  /**
   * Reset state
   */
  const reset = useCallback((): void => {
    setVendor(null);
    setError(null);
    setLoading(false);
  }, []);

  return {
    vendor,
    loading,
    error,
    fetchVendor,
    clearError,
    reset,
  };
};
